import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Spinner } from "~/components/ui/spinner";
import { QUERY_KEYS } from "~/constants/queryKeys";
import { apiClient } from "~/lib/orpc-client";
import Application from "~/components/application/wizard/application-wizard";
import { useTenantPath } from "~/hooks/use-tenant-path";

export const Route = createFileRoute("/$tenantSlug/_protected/apply/$applicationId")({
  component: TenantApplyRoute,
});

function TenantApplyRoute() {
  const { applicationId } = Route.useParams();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const isNew = applicationId === "new";

  const {
    data: application,
    isLoading: isLoadingApplication,
    error,
    isError,
    fetchStatus,
  } = useQuery({
    queryKey: QUERY_KEYS.APPLICATION_BY_ID(applicationId ?? ""),
    queryFn: () => apiClient.applications.getById({ id: Number(applicationId) }),
    enabled: !!applicationId && !isNew,
    retry: false,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  const isOwner = application?.isOwner !== false;
  const isInProgress = application?.applicationStatus === "IN_PROGRESS";

  useEffect(() => {
    if (application && !isOwner && !isInProgress && !isNew) {
      navigate({
        to: tenantPath(`/applications/${applicationId}`) as any,
        replace: true,
      });
    }
  }, [application, isOwner, isInProgress, isNew, applicationId, navigate, tenantPath]);

  if (isLoadingApplication && !application && !isNew) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <Spinner className="size-12" />
      </div>
    );
  }

  if (isError && !isNew && !application && fetchStatus === "idle") {
    console.error("Error loading application:", error);
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <div className="text-center">
          <p className="text-destructive font-medium mb-2">Error loading application</p>
          <p className="text-sm">{error?.message || "Unable to load application"}</p>
        </div>
      </div>
    );
  }

  if (!isOwner && !isInProgress && !isNew) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <Spinner className="size-12" />
      </div>
    );
  }

  return <Application application={application} isNew={isNew} />;
}
